<script setup lang="ts">
/**
 * Passwort selbst ändern (alle Rollen). Nach Erfolg läuft die Sitzung mit einem
 * frischen Token weiter, alle anderen Geräte werden abgemeldet.
 */
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth.store'

const emit = defineEmits<{ close: [] }>()

const authStore = useAuthStore()
const currentPassword = ref('')
const newPassword = ref('')
const repeatPassword = ref('')
const error = ref<string | null>(null)
const saving = ref(false)
const done = ref(false)

async function submit() {
  error.value = null
  if (newPassword.value.length < 8) {
    error.value = 'Das neue Passwort braucht mindestens 8 Zeichen'
    return
  }
  if (newPassword.value !== repeatPassword.value) {
    error.value = 'Die beiden neuen Passwörter stimmen nicht überein'
    return
  }
  saving.value = true
  try {
    await authStore.changePassword(currentPassword.value, newPassword.value)
    done.value = true
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Passwort konnte nicht geändert werden'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content" @click.stop>
      <h2>Passwort ändern</h2>

      <div v-if="done" class="modal-body">
        <div class="success-banner">Ihr Passwort wurde geändert. Auf anderen Geräten müssen Sie sich neu anmelden.</div>
        <div class="modal-actions">
          <button type="button" class="btn btn-primary" @click="emit('close')">Schließen</button>
        </div>
      </div>

      <form v-else class="modal-body" @submit.prevent="submit">
        <div v-if="error" class="error-banner">{{ error }}</div>
        <label>
          Aktuelles Passwort
          <input v-model="currentPassword" type="password" autocomplete="current-password" required />
        </label>
        <label>
          Neues Passwort (mindestens 8 Zeichen)
          <input v-model="newPassword" type="password" autocomplete="new-password" minlength="8" required />
        </label>
        <label>
          Neues Passwort wiederholen
          <input v-model="repeatPassword" type="password" autocomplete="new-password" minlength="8" required />
        </label>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="emit('close')">Abbrechen</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            {{ saving ? 'Speichert …' : 'Passwort ändern' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 100;
}
.modal-content {
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-content h2 { margin: 0 0 1rem; font-size: 1.125rem; color: #1e293b; }
.modal-body { display: flex; flex-direction: column; gap: 0.875rem; }
.modal-body label { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.8125rem; color: #334155; font-weight: 500; }
.modal-body input {
  box-sizing: border-box;
  height: 2.35rem;
  padding: 0.5rem 0.65rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 400;
}
.error-banner { padding: 0.75rem 1rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 0.5rem; color: #dc2626; font-size: 0.8125rem; }
.success-banner { padding: 0.75rem 1rem; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 0.5rem; color: #166534; font-size: 0.8125rem; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.5rem; }
.btn { padding: 0.5rem 1rem; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 500; border: 1px solid transparent; cursor: pointer; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: var(--brand-primary); color: white; }
.btn-secondary { background: white; border-color: #e2e8f0; color: #334155; }
</style>
